import type { ReactNode } from "react";
import { Tooltip, TooltipContent, TooltipTrigger, cn } from "@nexus/ui";

interface DrawerStatProps {
  label: string;
  tooltip: ReactNode;
  children: ReactNode;
  /** Grid column span for facts rows where one stat takes more horizontal space (e.g. an invite link). */
  colSpan?: 1 | 2 | 3;
  /** Override when the value isn't plain text (e.g. a status badge already carries its own typography). */
  valueClassName?: string;
  /** Override the tooltip's max width when the copy is longer than the default cell tooltips. */
  tooltipClassName?: string;
}

/** Single labeled stat cell for a drawer's facts row, with a tooltip explaining the metric. */
export function DrawerStat({
  label,
  tooltip,
  children,
  colSpan = 1,
  valueClassName = "text-sm font-semibold tabular-nums",
  tooltipClassName = "max-w-[220px]",
}: DrawerStatProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className={cn("flex cursor-default flex-col gap-1 px-4 py-3 text-left", colSpan === 2 && "col-span-2", colSpan === 3 && "col-span-3")}>
          <span className="text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
          <div className={valueClassName}>{children}</div>
        </div>
      </TooltipTrigger>
      <TooltipContent side="bottom" className={tooltipClassName}>{tooltip}</TooltipContent>
    </Tooltip>
  );
}
